import axios from 'axios'
import { getAuthorizedConfiguration } from '../helpers/RequestHelper'
import { CONTENT_ASSET_TYPE } from '../../views/directory/resources/ContentAssetTypeIcons'

export const OPEN_VIEWER = 'OPEN_VIEWER'
export const SET_VIEWER_BLOB_URL = 'SET_VIEWER_BLOB_URL'
export const SET_VIEWER_ERROR = 'SET_VIEWER_ERROR'
export const CLOSE_VIEWER = 'CLOSE_VIEWER'

export const STREAM_ENDPOINT = (assetToken) => `/content-assets/${encodeURIComponent(assetToken)}/stream`

export const openViewer = (asset, requestId) => ({
	type: OPEN_VIEWER,
	asset,
	requestId
})

export const setViewerBlobUrl = (blobUrl, requestId) => ({
	type: SET_VIEWER_BLOB_URL,
	blobUrl,
	requestId
})

export const setViewerError = (message, requestId) => ({
	type: SET_VIEWER_ERROR,
	message,
	requestId
})

export const closeViewer = () => ({
	type: CLOSE_VIEWER
})

/**
 * Open the viewer for an asset.
 *   - LINK: open the asset's ExternalUrl in a new tab and return. Never touches modal state.
 *   - All other types: dispatch OPEN_VIEWER, then issue ONE bearer-authenticated
 *     `axios.GET` for the stream bytes with `responseType: 'blob'`. Wrap the
 *     response Blob as a `blob:` URL via `URL.createObjectURL` and dispatch
 *     SET_VIEWER_BLOB_URL (success) or SET_VIEWER_ERROR (failure). The modal
 *     owns the `URL.revokeObjectURL` cleanup via a useEffect on the blobUrl.
 *
 * Stale-response guard (double): each open gets a fresh `requestId`. The thunk
 * checks the active `requestId` BEFORE calling `URL.createObjectURL`, so a stale
 * response is discarded without ever allocating a blob URL (no leak). The reducer
 * repeats the check on SET_VIEWER_BLOB_URL / SET_VIEWER_ERROR as defense in depth —
 * so if the user clicks card A and then card B before A's GET resolves, A's late
 * response is discarded on both layers.
 */
export const openAssetViewer = (asset) => {
	return async function(dispatch, getState) {
		if (!asset) return

		if (asset.Type === CONTENT_ASSET_TYPE.LINK) {
			const url = asset.ExternalUrl
			if (!isUsableUrl(url)) {
				// Surface a minimal console warning. A toast would be nicer; for
				// v1 we keep the FE surface tight and accept a quiet failure for
				// the rare malformed-URL case.
				console.warn('Directory: Link asset has no usable ExternalUrl. AssetId:', asset.Id ?? '(unknown)')
				return
			}
			window.open(url, '_blank', 'noopener,noreferrer')
			return
		}

		const requestId = Date.now()
		dispatch(openViewer(asset, requestId))

		try {
			const oidcUser = getState().oidc.user
			const { serviceManagementBackendUrl } = window.config
			const config = {
				...getAuthorizedConfiguration(oidcUser),
				responseType: 'blob'
			}
			const url = `${serviceManagementBackendUrl}${STREAM_ENDPOINT(asset.Token)}`
			const response = await axios.get(url, config)
			if (!response || !response.data) {
				dispatch(setViewerError("Couldn't load resource.", requestId))
				return
			}
			// Guard: if the user already opened another asset, discard this stale
			// response without allocating a blob URL that would never be revoked.
			const currentRequestId = getState().contentAssetViewer.get('requestId')
			if (currentRequestId !== requestId) {
				return
			}
			const blobUrl = URL.createObjectURL(response.data)
			dispatch(setViewerBlobUrl(blobUrl, requestId))
		} catch (error) {
			console.error('ContentAssetViewer: stream fetch failed', error)
			dispatch(setViewerError("Couldn't load resource.", requestId))
		}
	}
}

const isUsableUrl = (raw) => {
	if (!raw || typeof raw !== 'string') return false
	try {
		const u = new URL(raw)
		return u.protocol === 'http:' || u.protocol === 'https:'
	} catch (_) {
		return false
	}
}
